<template>
  <el-dialog
    :title="form.id ? '编辑拼团活动' : '新增拼团活动'"
    :visible="visible"
    width="94%"
    top="4vh"
    :close-on-click-modal="false"
    @close="$emit('update:visible', false)"
  >
    <el-alert
      v-if="lockedRules"
      title="活动已经开始，活动规则与时间保持不变；仍可调整商品配置及虚拟已拼数量。"
      type="warning"
      :closable="false"
      show-icon
      class="editor-alert"
    />
    <el-form ref="form" :model="form" :rules="rules" label-width="160px" class="activity-form">
      <el-row :gutter="24">
        <el-col :span="12">
          <el-form-item label="活动名称" prop="name">
            <el-input v-model.trim="form.name" maxlength="120" show-word-limit :disabled="lockedRules" />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="活动时间" prop="timeRange">
            <el-date-picker
              v-model="form.timeRange"
              type="datetimerange"
              range-separator="至"
              start-placeholder="开始时间"
              end-placeholder="结束时间"
              style="width:100%"
              :disabled="lockedRules"
            />
          </el-form-item>
        </el-col>
      </el-row>
      <el-row :gutter="24">
        <el-col :span="12">
          <el-form-item label="成团人数" prop="groupSize">
            <el-input-number v-model="form.groupSize" :min="2" :max="999" :disabled="lockedRules" />
            <span class="field-tip">至少 2 人，每位用户计 1 人</span>
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="拼团有效期（小时）" prop="groupDurationHours">
            <el-input-number v-model="form.groupDurationHours" :min="1" :max="24" :disabled="lockedRules" />
            <span class="field-tip">团长支付成功后开始计时，支持 1～24 小时</span>
          </el-form-item>
        </el-col>
      </el-row>
      <el-row :gutter="24">
        <el-col :span="12">
          <el-form-item label="虚拟已拼数量" prop="virtualSoldNum">
            <el-input-number v-model="form.virtualSoldNum" :min="0" :max="999999999" :precision="0" />
            <span class="field-tip">列表已拼份数 = 真实支付份数 + 虚拟已拼数量</span>
          </el-form-item>
        </el-col>
      </el-row>
      <el-row :gutter="24" class="switch-row">
        <el-col v-for="item in switchFields" :key="item.key" :span="6">
          <el-form-item :label="item.label">
            <el-switch v-model="form[item.key]" :disabled="lockedRules" />
            <div class="switch-tip">{{ item.tip }}</div>
          </el-form-item>
        </el-col>
      </el-row>
      <el-form-item label="活动商品" required>
        <el-button type="primary" icon="el-icon-plus" @click="selectorVisible = true">选择商品规格</el-button>
        <span class="field-tip">可选择多个商品和规格；选择后需逐个手动配置拼团价</span>
      </el-form-item>
    </el-form>

    <el-table :data="goodsRows" border size="small" row-key="goodsId" empty-text="请先选择活动商品">
      <el-table-column type="expand" width="48">
        <template slot-scope="scope">
          <el-table :data="scope.row.specs" border size="mini" class="spec-table">
            <el-table-column prop="specsName" label="商品规格" min-width="180" />
            <el-table-column prop="originalPrice" label="销售价" width="110" align="center" />
            <el-table-column label="拼团价（必填）" width="180" align="center">
              <template slot-scope="spec">
                <el-input-number
                  v-model="spec.row.groupPrice"
                  :min="0.01"
                  :precision="2"
                  :controls="false"
                  placeholder="请手动填写"
                  size="mini"
                />
              </template>
            </el-table-column>
            <el-table-column label="拼团库存" width="150" align="center">
              <template slot-scope="spec"><el-input-number v-model="spec.row.groupStock" :min="1" :max="spec.row.stock || 999999" :controls="false" size="mini" /></template>
            </el-table-column>
            <el-table-column label="单次限购" width="140" align="center">
              <template slot-scope="spec"><el-input-number v-model="spec.row.singleLimit" :min="0" :controls="false" size="mini" /></template>
            </el-table-column>
            <el-table-column label="总限购" width="140" align="center">
              <template slot-scope="spec"><el-input-number v-model="spec.row.totalLimit" :min="0" :controls="false" size="mini" /></template>
            </el-table-column>
            <el-table-column label="操作" width="90" align="center">
              <template slot-scope="spec"><el-button type="text" class="danger-text" @click="removeSpec(scope.row, spec.row)">删除</el-button></template>
            </el-table-column>
          </el-table>
        </template>
      </el-table-column>
      <el-table-column label="商品信息" min-width="300">
        <template slot-scope="scope">
          <div class="goods-cell"><el-image :src="scope.row.mainGraph" fit="cover" class="goods-image" /><div><div class="goods-name">{{ scope.row.goodsName }}</div><div class="muted">{{ scope.row.specs.length }} 个规格</div></div></div>
        </template>
      </el-table-column>
      <el-table-column label="价格区间" width="170" align="center">
        <template slot-scope="scope">{{ priceRange(scope.row.specs) }}</template>
      </el-table-column>
      <el-table-column label="拼团库存" width="130" align="center">
        <template slot-scope="scope">{{ totalStock(scope.row.specs) }}</template>
      </el-table-column>
      <el-table-column label="排序" width="150" align="center">
        <template slot-scope="scope"><el-input-number v-model="scope.row.sort" :min="0" :controls="false" size="mini" /></template>
      </el-table-column>
      <el-table-column label="操作" width="100" align="center">
        <template slot-scope="scope"><el-button type="text" class="danger-text" @click="removeGoods(scope.$index)">删除</el-button></template>
      </el-table-column>
    </el-table>

    <div slot="footer" class="dialog-footer">
      <el-button @click="$emit('update:visible', false)">取消</el-button>
      <el-button type="primary" :loading="saving" @click="submit">保存</el-button>
    </div>
    <SelectProductModal
      :visible.sync="selectorVisible"
      :selected-specs="flatSelectedSpecs"
      :multiple="true"
      @confirm="mergeSelectedSpecs"
    />
  </el-dialog>
</template>

<script>
import SelectProductModal from "@/views/seckill/SelectProductModal.vue";
import { getGroupBuyActivityDetail, getGroupBuyActivityGoods, saveGroupBuyActivity } from "@/api/groupBuy";

export default {
  name: "GroupBuyActivityEditor",
  components: { SelectProductModal },
  props: { visible: Boolean, activityId: { type: [Number, String], default: null } },
  data() {
    return {
      saving: false,
      selectorVisible: false,
      goodsRows: [],
      form: this.defaultForm(),
      rules: {
        name: [{ required: true, message: "请输入活动名称", trigger: "blur" }],
        timeRange: [{ type: "array", required: true, len: 2, message: "请选择活动时间", trigger: "change" }],
        groupSize: [{ required: true, message: "请设置成团人数", trigger: "change" }],
        groupDurationHours: [{ required: true, message: "请设置拼团有效期", trigger: "change" }],
        virtualSoldNum: [{ required: true, message: "请设置虚拟已拼数量", trigger: "change" }]
      },
      switchFields: [
        { key: "virtualEnabled", label: "支持虚拟成团", tip: "到期未满时由系统补足虚拟成员" },
        { key: "directJoinEnabled", label: "允许直接参团", tip: "关闭后仅可通过邀请链接参加" },
        { key: "couponEnabled", label: "允许使用优惠券", tip: "拼团结算时是否可叠加优惠券" },
        { key: "refundBeforeSuccessEnabled", label: "成团前主动退款", tip: "团长退款将导致该团失败" }
      ]
    };
  },
  computed: {
    lockedRules() { return !!(this.form.id && this.form.startTime && this.form.startTime <= Date.now()); },
    flatSelectedSpecs() {
      return this.goodsRows.reduce((list, goods) => list.concat(goods.specs.map(spec => ({ ...spec, goodsId: goods.goodsId }))), []);
    }
  },
  watch: {
    visible(val) { if (val) this.load(); }
  },
  methods: {
    defaultForm() { return { id: null, name: "", timeRange: [], startTime: null, endTime: null, groupSize: 2, groupDurationHours: 24, virtualSoldNum: 0, virtualEnabled: true, directJoinEnabled: true, couponEnabled: true, refundBeforeSuccessEnabled: true }; },
    async load() {
      this.form = this.defaultForm(); this.goodsRows = [];
      if (!this.activityId) return;
      const [detail, goods] = await Promise.all([getGroupBuyActivityDetail({ id: this.activityId }), getGroupBuyActivityGoods({ activityId: this.activityId })]);
      this.form = { ...this.defaultForm(), ...detail.data, timeRange: [new Date(detail.data.startTime), new Date(detail.data.endTime)] };
      this.goodsRows = (goods.data || []).map(item => ({ ...item, specs: (item.specs || []).map(spec => ({ ...spec, specsName: spec.specsName, price: spec.originalPrice, stock: spec.stock })) }));
    },
    mergeSelectedSpecs(rows) {
      const existing = {}; const existingGoods = {}; this.goodsRows.forEach(goods => { existingGoods[goods.goodsId] = goods; goods.specs.forEach(spec => { existing[spec.specsId] = spec; }); });
      const grouped = {};
      rows.forEach(row => {
        const goodsId = row.goodsId; const oldGoods = existingGoods[goodsId]; if (!grouped[goodsId]) grouped[goodsId] = { goodsId, goodsName: row.goodsName || (oldGoods && oldGoods.goodsName) || "商品", mainGraph: row.mainGraph || (oldGoods && oldGoods.mainGraph), sort: oldGoods ? oldGoods.sort : this.goodsRows.length, specs: [] };
        const old = existing[row.id] || existing[row.specsId];
        grouped[goodsId].specs.push({ specsId: row.id || row.specsId, specsName: row.combNames || row.specsName, originalPrice: row.price, price: row.price, stock: row.stock, groupPrice: old ? old.groupPrice : null, groupStock: old ? old.groupStock : Math.max(1, Math.min(Number(row.stock) || 1, 100)), soldStock: old ? old.soldStock : 0, singleLimit: old ? old.singleLimit : 0, totalLimit: old ? old.totalLimit : 0 });
      });
      this.goodsRows = Object.keys(grouped).map(key => grouped[key]);
    },
    removeSpec(goods, spec) { goods.specs = goods.specs.filter(item => item.specsId !== spec.specsId); if (!goods.specs.length) this.goodsRows = this.goodsRows.filter(item => item.goodsId !== goods.goodsId); },
    removeGoods(index) { this.goodsRows.splice(index, 1); },
    priceRange(specs) {
      if (!specs.length) return "-";
      if (specs.some(item => item.groupPrice === null || item.groupPrice === undefined || item.groupPrice === "")) return "待配置";
      const values = specs.map(item => Number(item.groupPrice));
      const min = Math.min(...values).toFixed(2); const max = Math.max(...values).toFixed(2);
      return min === max ? `¥${min}` : `¥${min}～${max}`;
    },
    totalStock(specs) { return specs.reduce((sum, item) => sum + Number(item.groupStock || 0), 0); },
    submit() {
      this.$refs.form.validate(async valid => {
        if (!valid) return;
        if (!this.goodsRows.length || this.goodsRows.some(item => !item.specs.length)) return this.$message.warning("请至少选择一个活动商品规格");
        const missingPrice = this.goodsRows.some(goods => goods.specs.some(spec => !spec.groupPrice));
        if (missingPrice) return this.$message.warning("请为每个商品规格手动填写拼团价");
        const invalid = this.goodsRows.some(goods => goods.specs.some(spec => !spec.groupStock || (spec.totalLimit > 0 && spec.singleLimit > spec.totalLimit)));
        if (invalid) return this.$message.warning("请检查拼团库存和限购数量");
        const payload = { ...this.form, startTime: new Date(this.form.timeRange[0]).getTime(), endTime: new Date(this.form.timeRange[1]).getTime(), goodsJson: JSON.stringify(this.goodsRows.map(goods => ({ goodsId: goods.goodsId, sort: goods.sort, specs: goods.specs.map(spec => ({ specsId: spec.specsId, groupPrice: spec.groupPrice, groupStock: spec.groupStock, singleLimit: spec.singleLimit || 0, totalLimit: spec.totalLimit || 0 })) }))) };
        delete payload.timeRange; if (!payload.id) delete payload.id;
        this.saving = true;
        try {
          const result = await saveGroupBuyActivity(payload);
          if (!result || result.bizcode !== 100) return;
          this.$message.success("拼团活动保存成功");
          this.$emit("saved");
          this.$emit("update:visible", false);
        }
        finally { this.saving = false; }
      });
    }
  }
};
</script>

<style scoped lang="scss">
.editor-alert { margin-bottom: 20px; }
.activity-form { padding: 4px 8px 0; }
.field-tip, .switch-tip, .muted { color: #909399; font-size: 12px; }
.field-tip { margin-left: 12px; }
.switch-tip { line-height: 1.45; margin-top: 5px; }
.switch-row { padding: 16px 0 2px; background: #fafafa; margin-bottom: 18px; }
.goods-cell { display: flex; align-items: center; gap: 12px; }
.goods-image { width: 54px; height: 54px; border-radius: 4px; flex: none; }
.goods-name { color: #303133; font-weight: 500; line-height: 1.5; }
.spec-table { padding: 8px 48px; }
.danger-text { color: #f56c6c; }
::v-deep .el-dialog__body { max-height: 78vh; overflow-y: auto; }
</style>
